import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { milestonesGridActions } from 'app/pages/MilestonesGrid/slice';
import { commentActions } from 'app/components/SlideOutPanel/CommentPanel/CommentState';
import { selectCurrentCommentId } from 'app/components/SlideOutPanel/CommentPanel/CommentState/selectors';
import {
  mergeViewStateSearchParams,
  buildPathPreservingQueryParams
} from 'utils/sharedViewMode';

export interface SlideoutUrlHook {
  isSlideoutOpen: boolean;
  openSlideout: (
    issueId: string,
    commentId?: string,
    releasePhaseId?: string,
    queryParams?: string
  ) => void;
  closeSlideout: () => void;
  currentIssueId: string | null;
  currentCommentId: string | null;
  currentReleasePhaseId: string | null;
}

/** Extracts issueId from a pathname like /moments/:id or /milestones/moments/:id */
function issueIdFromPathname(pathname: string): string | null {
  const match = pathname.match(/\/moments\/([^/?#]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

export const useSlideoutUrl = (): SlideoutUrlHook => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const dispatch = useDispatch();

  // Initialize synchronously from the URL so consumers never see a stale null on
  // the first render. This is important for components (e.g. AIAssistantDock) that
  // live outside <Routes> where useParams() always returns {}.
  const [currentIssueId, setCurrentIssueId] = useState<string | null>(() => {
    if (params.issueId) return params.issueId;
    const fromPath = issueIdFromPathname(location.pathname);
    if (fromPath) return fromPath;
    const sp = new URLSearchParams(location.search);
    return sp.get('moment') || sp.get('card') || null;
  });

  const currentCommentId = useSelector(selectCurrentCommentId);
  const [currentReleasePhaseId, setCurrentReleasePhaseId] = useState<
    string | null
  >(null);

  // Parse the current URL to check if slideout should be open
  const getIssueIdFromUrl = useCallback(() => {
    // Use React Router params instead of regex parsing
    const issueId = params.issueId;
    const commentId = params.commentId;

    if (issueId) {
      dispatch(commentActions.setCurrentCommentId(commentId || null));

      // Check for releasePhaseId in URL query parameters
      const searchParams = new URLSearchParams(location.search);
      const phaseId = searchParams.get('phaseId');
      if (phaseId) {
        setCurrentReleasePhaseId(phaseId);
      } else {
        setCurrentReleasePhaseId(null);
      }

      return issueId;
    }

    // Path-based fallback for hooks called outside <Routes> (e.g. AIAssistantDock)
    // where useParams() always returns {}.
    const fromPath = issueIdFromPathname(location.pathname);
    if (fromPath) {
      const searchParams = new URLSearchParams(location.search);
      const phaseId = searchParams.get('phaseId');
      setCurrentReleasePhaseId(phaseId || null);
      return fromPath;
    }

    // Fallback to query parameter for backward compatibility
    const searchParams = new URLSearchParams(location.search);
    const paramId = searchParams.get('moment') || searchParams.get('card');
    if (paramId) {
      dispatch(commentActions.setCurrentCommentId(null));
      setCurrentReleasePhaseId(null);
    }
    return paramId;
  }, [
    dispatch,
    params.issueId,
    params.commentId,
    location.pathname,
    location.search
  ]);

  // Initialize state from URL on component mount and URL changes
  useEffect(() => {
    const issueIdFromUrl = getIssueIdFromUrl();
    setCurrentIssueId(issueIdFromUrl);
  }, [getIssueIdFromUrl]);

  const isSlideoutOpen = Boolean(currentIssueId);

  const openSlideout = useCallback(
    (
      issueId: string,
      commentId?: string,
      releasePhaseId?: string,
      queryParams?: string
    ) => {
      // Check which page we're opening the slide-out from, so closing it
      // returns to that same page instead of always falling back to Roadmap.
      const isFromMilestones = location.pathname.includes('/milestones');
      const isFromReviewApproval =
        location.pathname.includes('/review-approval');
      const baseRoute = isFromMilestones
        ? '/milestones/moments'
        : isFromReviewApproval
        ? '/review-approval/moments'
        : '/moments';

      // Use clean moment URL structure with optional comment ID
      const momentUrl = commentId
        ? `${baseRoute}/${encodeURIComponent(issueId)}/${encodeURIComponent(
            commentId
          )}`
        : `${baseRoute}/${encodeURIComponent(issueId)}`;

      // Preserve shared-view and filter query params from the current location
      const searchParams = mergeViewStateSearchParams(
        location.search,
        queryParams
      );
      if (releasePhaseId) {
        searchParams.set('phaseId', releasePhaseId);
      }

      const fullUrl = searchParams.toString()
        ? `${momentUrl}?${searchParams.toString()}`
        : momentUrl;

      // Navigate to the moment-specific URL
      navigate(fullUrl, { replace: false });

      setCurrentIssueId(issueId);
      dispatch(commentActions.setCurrentCommentId(commentId || null));
      setCurrentReleasePhaseId(releasePhaseId || null);
    },
    [dispatch, navigate, location.pathname, location.search]
  );

  const closeSlideout = useCallback(() => {
    const isFromMilestones = location.pathname.includes('/milestones');
    const isFromReviewApproval = location.pathname.includes('/review-approval');
    const baseUrl = isFromMilestones
      ? '/milestones'
      : isFromReviewApproval
      ? '/review-approval'
      : '/';
    const fullUrl = buildPathPreservingQueryParams(baseUrl, location.search);

    navigate(fullUrl, { replace: false });

    // Refresh milestones data if closing from milestones route
    // This ensures latest data is shown after potential edits in RSD
    if (isFromMilestones) {
      dispatch(milestonesGridActions.loadMilestonesData({}));
    }

    setCurrentIssueId(null);
    dispatch(commentActions.setCurrentCommentId(null));
    setCurrentReleasePhaseId(null);
  }, [navigate, location.search, location.pathname, dispatch]);

  return {
    isSlideoutOpen,
    openSlideout,
    closeSlideout,
    currentIssueId,
    currentCommentId,
    currentReleasePhaseId
  };
};
